import React, { useMemo } from 'react';
import { styled } from '@linaria/react';
import { v4 as uuid } from 'uuid';
import { useAtom } from 'jotai';
import { focusAtom } from 'jotai/optics';
import useTranslation from 'next-translate/useTranslation';
import { TypeOf } from 'yup';
import Button from '../../../components/Button';
import { FormControl, Text, Modal } from '@spaceshipapp/cambridge';
import { itemMapAtom, parcelItemsAtom } from '../../../contexts/shipment';
import Currency from '../../../utils/currency';
import itemSchema from '../../../schemas/ship/item';
import ItemForm from '../../ItemForm';
import ItemBtn from './ItemBtn';
import { ItemFormModalContainer } from './styled';

interface Props {
  parcelId: string;
}

const Items: React.FC<Props> = ({ parcelId }) => {
  const { t } = useTranslation('common');
  const itemsAtom = useMemo(
    () => focusAtom(parcelItemsAtom, (o) => o.prop(parcelId)),
    [parcelItemsAtom],
  );
  const [, setItemMap] = useAtom(itemMapAtom);
  const [items, setItems] = useAtom(itemsAtom);

  const handleAddItem = (data: TypeOf<typeof itemSchema>) => {
    const itemId = uuid();
    const {
      currency,
      naturalAmount,
      description,
      qty,
      hkhsCode,
      isIncludesFood,
      isIncludesBattery,
      countryOfManufacture,
      remarks,
    } = data;
    const value = Currency.convert(naturalAmount, currency);
    setItemMap((prev) => ({
      ...prev,
      [itemId]: {
        description,
        value,
        qty,
        hkhsCode,
        isIncludesFood,
        isIncludesBattery,
        countryOfManufacture,
        remarks,
      },
    }));
    setItems((prev = []) => [...prev, itemId]);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems((prev = []) => prev.filter((id) => id !== itemId));
  };

  if (!parcelId) return null;

  return (
    <FormControl>
      <Text as="label">{t('Items.formLabelItems')}</Text>
      <ItemsGroup>
        {(items || []).map((itemId) => (
          <ItemBtn key={itemId} itemId={itemId} onRemove={() => handleRemoveItem(itemId)} />
        ))}
        <ItemFormModalContainer>
          <Modal
            title={t('Items.editItemTitle')}
            trigger={
              <Button
                size="sm"
                iconName="Plus"
                data-tracking-label={t('commonEn:Items.buttonOpenAddItem')}
              >
                {t('Items.buttonOpenAddItem')}
              </Button>
            }
          >
            <ItemForm onSubmit={handleAddItem} />
          </Modal>
        </ItemFormModalContainer>
      </ItemsGroup>
    </FormControl>
  );
};

const ItemsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-top: 1rem;
  /* flex gap fix */
  margin-bottom: -0.5rem;

  > * {
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

export default Items;
